/**
 * PATH: packages/ui/src/navigation/useSmartBack.ts
 * FILE_ID: UI-NAV-SMARTBACK-001
 * PURPOSE: "Go back to where you came from" navigation primitive shared by the
 *   global header Back button and any page-level back affordance.
 * WHY: operator 2026-06-29 — the app had NO global back control and the few
 *   page-level backs were hard-coded to a fixed route (e.g. Job detail →
 *   /jobs), so arriving from a Report / Kanban / search lost your place.
 * FLOW:
 *   router navigations ─ useTrackInAppNav ─▶ in-app depth (module singleton)
 *   in-app depth ─ useInAppCanGoBack ─┐
 *                                     ├─ useSmartBack.goBack(): navigate(-1) | fallback
 *   fallbackHref ─────────────────────┘
 * DEPENDENCIES:
 *   - react-router-dom: useNavigate / useLocation / useNavigationType (a Router
 *     context is required; callers in non-router hosts must guard with
 *     useInRouterContext — see components/header/BackButton.tsx).
 */
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'

// ---------------------------------------------------------------------------
// In-app navigation depth — a per-SPA module singleton.
//
// Why a singleton (not component state): the master shell force-remounts page
// content on every navigation (`<div key={location.pathname}>` in src/App.jsx),
// so a depth counter kept in a page component would reset to 0 on every screen
// and wrongly report "nothing to go back to". The Header (where the counter is
// driven) persists across navigations, but page-level consumers (Job detail)
// remount — so the depth must live OUTSIDE React, here, where every consumer in
// the same bundle reads one shared value.
// ---------------------------------------------------------------------------
let inAppDepth = 0
const listeners = new Set<() => void>()

function setDepth(next: number): void {
  const clamped = next < 0 ? 0 : next
  if (clamped === inAppDepth) return
  inAppDepth = clamped
  listeners.forEach((l) => l())
}

function getDepth(): number {
  return inAppDepth
}

/**
 * React Router stores its stack position on `history.state.idx`. A positive
 * index means `navigate(-1)` stays inside this SPA session. Index 0 is the
 * first document; popping it would leave the app.
 *
 * The header often mounts only after login has already pushed Jobs Dashboard,
 * so the depth counter never saw that push. The index is the source of truth
 * when the router wrote one.
 */
export function inAppHistoryCanPop(
  depth: number,
  historyState: { idx?: unknown } | null,
): boolean {
  if (historyState && typeof historyState.idx === 'number' && Number.isFinite(historyState.idx)) {
    return historyState.idx > 0
  }
  return depth > 0
}

function historyState(): { idx?: unknown } | null {
  if (typeof window === 'undefined') return null
  const state = window.history.state as { idx?: unknown } | null
  return state && typeof state === 'object' ? state : null
}

/** True when an in-app href is the screen already on screen. */
export function hrefPointsAt(href: string, pathname: string, search: string): boolean {
  if (!href || href.startsWith('http')) return false
  const hashAt = href.indexOf('#')
  const bare = hashAt >= 0 ? href.slice(0, hashAt) : href
  const queryAt = bare.indexOf('?')
  const path = queryAt >= 0 ? bare.slice(0, queryAt) : bare
  const query = queryAt >= 0 ? bare.slice(queryAt) : ''
  if (path !== pathname) return false
  if (!query) return true
  return query === search
}

/** Test-only: reset the shared depth between cases. */
export function resetInAppNavDepth(): void {
  setDepth(0)
}

// ---------------------------------------------------------------------------
// Tracking — call ONCE from a component that stays mounted for the SPA's life
// (the shared Header). It observes each history transition and adjusts depth:
// PUSH deepens (new screen), POP unwinds (back/forward to an earlier screen),
// REPLACE leaves it unchanged (auto-resume redirects, recordLastRoute, etc.).
// ---------------------------------------------------------------------------
export function useTrackInAppNav(): void {
  const location = useLocation()
  const navType = useNavigationType()
  const initializedRef = useRef(false)
  const lastKeyRef = useRef<string | null>(null)

  useEffect(() => {
    // Skip the first observation: the initial render is reported as POP and is
    // the session's starting screen, not a real back.
    if (!initializedRef.current) {
      initializedRef.current = true
      lastKeyRef.current = location.key
      return
    }
    // Guard against double-invocation for the same history entry (e.g. React
    // StrictMode's dev-only effect re-run) so we don't double-count a screen.
    if (lastKeyRef.current === location.key) return
    lastKeyRef.current = location.key

    if (navType === 'PUSH') setDepth(getDepth() + 1)
    else if (navType === 'POP') setDepth(getDepth() - 1)
    // REPLACE: depth unchanged.
  }, [location.key, navType])
}

/**
 * True when `navigate(-1)` will land on a previous entry WITHIN this SPA's
 * session — so going back stays inside the app and won't reload the previous
 * document / escape cross-origin. Subscribes to the shared depth so the Back
 * button shows/hides reactively.
 */
export function useInAppCanGoBack(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange)
      return () => listeners.delete(onChange)
    },
    () => inAppHistoryCanPop(getDepth(), historyState()),
    () => false,
  )
}

export interface SmartBack {
  /** Whether there is in-app history to pop. */
  canGoBack: boolean
  /** Pop in-app history if possible, else navigate to `fallbackHref` (if given). */
  goBack: () => void
}

/**
 * Returns a `goBack` that prefers "where you came from" (`navigate(-1)`) and
 * falls back to a stable parent route when there is nothing in-app to pop
 * (deep-link / first screen of the session). Never a dead end, never escapes
 * the SPA via the browser's cross-origin back.
 */
export function useSmartBack(fallbackHref?: string): SmartBack {
  const navigate = useNavigate()
  const location = useLocation()
  const canGoBack = useInAppCanGoBack()
  // Home's crumb is /jobs-dashboard. On that screen the crumb is not a
  // destination; navigating to it looks like a dead click.
  const fallback = fallbackHref && !hrefPointsAt(fallbackHref, location.pathname, location.search)
    ? fallbackHref
    : undefined
  const goBack = useCallback(() => {
    if (canGoBack) navigate(-1)
    else if (fallback) navigate(fallback)
  }, [canGoBack, fallback, navigate])
  return { canGoBack, goBack }
}
